import { CallToolResult } from '@modelcontextprotocol/sdk/types.js';
import { Ok } from 'ts-results-es';
import { z } from 'zod';

import {
  ArgsValidationError,
  DatasourceNotAllowedError,
  FeatureDisabledError,
} from '../../../errors/mcpToolError.js';
import { useRestApi } from '../../../restApiInstance.js';
import { GraphQLResponse } from '../../../sdks/tableau/apis/metadataApi.js';
import { ProductVersion } from '../../../sdks/tableau/types/serverInfo.js';
import { WebMcpServer } from '../../../server.web.js';
import { getResultForTableauVersion } from '../../../utils/isTableauVersionAtLeast.js';
import { getVizqlDataServiceDisabledError } from '../getVizqlDataServiceDisabledError.js';
import { resourceAccessChecker } from '../resourceAccessChecker.js';
import { ToolRules, WebTool } from '../tool.js';
import { combineFields, simplifyReadMetadataResult } from './datasourceMetadataUtils.js';

export const getGraphqlQuery = (datasourceLuid: string): string => `
  query datasourceFieldInfo {
    publishedDatasources(filter: { luid: "${datasourceLuid}" }) {
      name
      description
      owner {
        name
      }
      fields {
        name
        isHidden
        description
        descriptionInherited {
          attribute
          value
        }
        fullyQualifiedName
        __typename
        upstreamTables {
          name
        }
        ... on AnalyticsField {
          __typename
        }
        ... on ColumnField {
          dataCategory
          role
          dataType
          defaultFormat
          semanticRole
          aggregation
          aggregationParam
        }
        ... on CalculatedField {
          dataCategory
          role
          dataType
          defaultFormat
          semanticRole
          aggregation
          aggregationParam
          formula
          isAutoGenerated
          hasUserReference
        }
        ... on BinField {
          dataCategory
          role
          dataType
          formula
          binSize
        }
        ... on GroupField {
          dataCategory
          role
          dataType
          hasOther
        }
        ... on CombinedSetField {
          delimiter
          combinationType
        }
      }
    }
  }`;

const paramsSchema = {
  datasourceLuid: z.string().nonempty(),
};

export type GetDatasourceMetadataError =
  | {
      type: 'feature-disabled';
    }
  | {
      type: 'datasource-not-allowed';
      message: string;
    };

export const getGetDatasourceMetadataTool = (
  server: WebMcpServer,
  productVersion: ProductVersion,
): WebTool<typeof paramsSchema> => {
  const rules = getDatasourceMetadataRules(productVersion);
  const getDatasourceMetadataTool = new WebTool({
    server,
    name: 'get-datasource-metadata',
    description: `
    This tool retrieves metadata for a specified datasource by taking the basic, high level, metadata results from Tableau's VizQL Data Service and enriches them with additional context provided by Tableau's Metadata API.
    The metadata provided by this tool consists of the datasource model, fields, and parameters that belong to the datasource.
    Fields will contain properties such as name and dataType, but may also expose richer context such as descriptions, dataCategories, roles, etc.
    This tool should be used for getting the metadata to ground the use of a tool that queries Tableau published data sources.
    `,
    paramsSchema,
    annotations: {
      title: 'Get Datasource Metadata',
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
    callback: async ({ datasourceLuid }, extra): Promise<CallToolResult> => {
      const query = getGraphqlQuery(datasourceLuid);

      return await getDatasourceMetadataTool.logAndExecute({
        extra,
        args: { datasourceLuid },
        callback: async () => {
          if (!datasourceLuid) {
            return new ArgsValidationError('datasourceLuid must be a non-empty string.').toErr();
          }
          const configWithOverrides = await extra.getConfigWithOverrides();

          const isDatasourceAllowedResult = await resourceAccessChecker.isDatasourceAllowed({
            datasourceLuid,
            extra,
          });

          if (!isDatasourceAllowedResult.allowed) {
            return new DatasourceNotAllowedError(isDatasourceAllowedResult.message).toErr();
          }

          return await useRestApi({
            ...extra,
            jwtScopes: getDatasourceMetadataTool.requiredApiScopes,
            callback: async (restApi) => {
              // Fetching metadata from VizQL Data Service API.
              const readMetadataResult = await restApi.vizqlDataServiceMethods.readMetadata({
                datasource: {
                  datasourceLuid,
                },
              });

              if (readMetadataResult.isErr()) {
                return new FeatureDisabledError(getVizqlDataServiceDisabledError()).toErr();
              }

              // Fetching datasource model from VizQL Data Service API.
              const datasourceModelResult = !rules.datasourceModelIsUnavailable
                ? await restApi.vizqlDataServiceMethods.getDatasourceModel({
                    datasource: {
                      datasourceLuid,
                    },
                  })
                : undefined;

              if (datasourceModelResult && datasourceModelResult.isErr()) {
                return new FeatureDisabledError(getVizqlDataServiceDisabledError()).toErr();
              }

              if (configWithOverrides.disableMetadataApiRequests) {
                // Exit early since requests to the Tableau Metadata API are disabled.
                return Ok(
                  simplifyReadMetadataResult(
                    readMetadataResult.value,
                    datasourceModelResult?.value,
                  ),
                );
              }

              let listFieldsResult: GraphQLResponse;

              try {
                // Fetching metadata from Tableau Metadata API.
                // Using try-catch here since requests could fail if the service is not enabled.
                listFieldsResult = await restApi.metadataMethods.graphql(query);
              } catch {
                return Ok(
                  simplifyReadMetadataResult(
                    readMetadataResult.value,
                    datasourceModelResult?.value,
                  ),
                );
              }

              // Combine the results from the VizQL Data Service API and the Tableau Metadata API.
              return Ok(
                combineFields(
                  readMetadataResult.value,
                  listFieldsResult,
                  datasourceModelResult?.value,
                ),
              );
            },
          });
        },
        constrainSuccessResult: (fields) => {
          return {
            type: 'success',
            result: fields,
          };
        },
      });
    },
  });

  return getDatasourceMetadataTool;
};

function getDatasourceMetadataRules(productVersion: ProductVersion): ToolRules {
  return getResultForTableauVersion({
    productVersion,
    mappings: {
      '2025.3.0': {},
      default: {
        datasourceModelIsUnavailable: true,
      },
    },
  });
}
